/**
 * Document Management Module - Document List Page
 * 
 * Displays all documents with filtering, search, and categorization capabilities.
 * Provides quick access to document details and actions.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import {
  FaFilePdf,
  FaFileWord,
  FaEye,
  FaEdit,
  FaClock,
  FaCheckCircle,
  FaExclamationCircle,
  FaBan,
  FaFilter,
  FaSearch,
  FaUpload,
  FaFolder,
  FaList,
  FaSitemap,
} from 'react-icons/fa';
import { MdOutlineDescription } from 'react-icons/md';
import { Document, DocumentCategory, Framework, DocumentStatus } from './types';
import documentService from './documentService';
import FileStructure from './FileStructure';
import DocumentChat from './DocumentChat';
import './DocumentManagement.css';

const DocumentList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | 'All'>('All');
  const [selectedFramework, setSelectedFramework] = useState<Framework | 'All'>('All');
  const [selectedStatus, setSelectedStatus] = useState<DocumentStatus | 'All'>('All');
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list'); // Toggle between list and tree view

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [documents, searchTerm, selectedCategory, selectedFramework, selectedStatus]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const docs = await documentService.getDocuments();
      setDocuments(docs);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...documents];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        doc => {
          // Standard text search
          const textMatch =
            doc.metadata.title.toLowerCase().includes(term) ||
            doc.metadata.description?.toLowerCase().includes(term) ||
            doc.metadata.controlId?.toLowerCase().includes(term) ||
            doc.metadata.category?.toLowerCase().includes(term) ||
            doc.metadata.framework?.toLowerCase().includes(term) ||
            doc.metadata.status?.toLowerCase().includes(term) ||
            doc.metadata.documentOwnerName?.toLowerCase().includes(term) ||
            (doc.metadata.tags && Array.isArray(doc.metadata.tags) && 
              doc.metadata.tags.some((tag: string) => tag.toLowerCase().includes(term)));

          // Expiry date search
          let expiryMatch = false;
          if (term === 'expired' || term.includes('expired')) {
            if (doc.metadata.expiryDate) {
              const expiryDate = doc.metadata.expiryDate instanceof Date 
                ? doc.metadata.expiryDate 
                : new Date(doc.metadata.expiryDate);
              expiryMatch = expiryDate < new Date() || doc.metadata.status === 'Expired';
            } else {
              expiryMatch = doc.metadata.status === 'Expired';
            }
          } else if (term === 'expiring' || term.includes('expiring soon')) {
            if (doc.metadata.expiryDate) {
              const expiryDate = doc.metadata.expiryDate instanceof Date 
                ? doc.metadata.expiryDate 
                : new Date(doc.metadata.expiryDate);
              const thirtyDaysFromNow = new Date();
              thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
              expiryMatch = expiryDate >= new Date() && expiryDate <= thirtyDaysFromNow;
            }
          } else if (doc.metadata.expiryDate) {
            // Try to match date format in expiry date
            const expiryDate = doc.metadata.expiryDate instanceof Date 
              ? doc.metadata.expiryDate 
              : new Date(doc.metadata.expiryDate);
            const dateStr = expiryDate.toLocaleDateString('en-US');
            expiryMatch = dateStr.toLowerCase().includes(term);
          }

          return textMatch || expiryMatch;
        }
      );
    }

    // Category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(doc => doc.metadata.category === selectedCategory);
    }

    // Framework filter
    if (selectedFramework !== 'All') {
      filtered = filtered.filter(doc => doc.metadata.framework === selectedFramework);
    }

    // Status filter
    if (selectedStatus !== 'All') {
      filtered = filtered.filter(doc => doc.metadata.status === selectedStatus);
    }

    setFilteredDocuments(filtered);
  };

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'Published':
      case 'Approved':
        return <FaCheckCircle className="text-success" />;
      case 'Under Review':
        return <FaClock className="text-warning" />;
      case 'Draft':
        return <FaEdit className="text-info" />;
      case 'Expired':
        return <FaExclamationCircle className="text-danger" />;
      default:
        return <FaBan className="text-secondary" />;
    }
  };

  const getStatusBadgeClass = (status: DocumentStatus) => {
    switch (status) {
      case 'Published':
      case 'Approved':
        return 'badge bg-success';
      case 'Under Review':
        return 'badge bg-warning text-dark';
      case 'Draft':
        return 'badge bg-info';
      case 'Expired':
        return 'badge bg-danger';
      default:
        return 'badge bg-secondary';
    }
  };

  const getFileIcon = (fileName: string) => {
    if (fileName.toLowerCase().endsWith('.pdf')) {
      return <FaFilePdf className="text-danger" size={24} />;
    } else if (fileName.toLowerCase().endsWith('.docx') || fileName.toLowerCase().endsWith('.doc')) {
      return <FaFileWord className="text-primary" size={24} />;
    }
    return <MdOutlineDescription size={24} />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isExpiringSoon = (expiryDate?: Date | string): boolean => {
    if (!expiryDate) return false;
    // Convert to Date if it's a string
    const date = expiryDate instanceof Date ? expiryDate : new Date(expiryDate);
    if (isNaN(date.getTime())) return false; // Invalid date
    const daysUntilExpiry = Math.floor(
      (date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  };

  const isExpired = (expiryDate?: Date | string): boolean => {
    if (!expiryDate) return false;
    // Convert to Date if it's a string
    const date = expiryDate instanceof Date ? expiryDate : new Date(expiryDate);
    if (isNaN(date.getTime())) return false; // Invalid date
    return date < new Date();
  };

  if (loading) {
    return (
      <div className="document-management-container">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="document-management-container">
      <div className="container-fluid p-4" style={{ paddingTop: '2rem', marginTop: '100px', zoom: 0.9 }}>
        {/* Breadcrumb */}
        <Breadcrumb>
          <BreadcrumbItem>{t('header.you_are_here')}</BreadcrumbItem>
          <span className="mx-2">/</span>
          <BreadcrumbItem active style={{ paddingRight: '10px' }}>
            Document Management
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Document Management</h5>
          <div className="d-flex gap-2 align-items-center">
            {/* View Mode Toggle */}
            <div 
              className="d-flex align-items-center"
              style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                padding: '4px',
                border: '1px solid #dee2e6',
                gap: '4px'
              }}
              role="group"
            >
              <button
                type="button"
                onClick={() => setViewMode('list')}
                title="List View"
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: viewMode === 'list' ? '#0d6efd' : 'transparent',
                  color: viewMode === 'list' ? '#ffffff' : '#495057',
                  fontWeight: viewMode === 'list' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: viewMode === 'list' ? '0 2px 4px rgba(13, 110, 253, 0.2)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (viewMode !== 'list') {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                  }
                }}
                onMouseLeave={(e) => {
                  if (viewMode !== 'list') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <FaList size={16} />
                <span>List</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('tree')}
                title="Tree View"
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: viewMode === 'tree' ? '#0d6efd' : 'transparent',
                  color: viewMode === 'tree' ? '#ffffff' : '#495057',
                  fontWeight: viewMode === 'tree' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: viewMode === 'tree' ? '0 2px 4px rgba(13, 110, 253, 0.2)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (viewMode !== 'tree') {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                  }
                }}
                onMouseLeave={(e) => {
                  if (viewMode !== 'tree') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <FaSitemap size={16} />
                <span>Tree</span>
              </button>
            </div>
            <button
              className="btn btn-primary btn-lg d-flex align-items-center gap-2 shadow-sm"
              onClick={() => navigate('/documents/upload')}
              style={{ fontWeight: '600', whiteSpace: 'nowrap', zIndex: 10 }}
            >
              <FaUpload size={18} /> Create New Document
            </button>
          </div>
        </div>

        {/* Quick Action Button - Always Visible */}
         

        {/* Filters - Show in both list and tree view */}
        {(
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3">
              {/* Search */}
              <div className="col-md-4">
                <label className="form-label">
                  <FaSearch className="me-2" />
                  Search
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by title, description, or control ID..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Category Filter */}
              <div className="col-md-2">
                <label className="form-label">
                  <FaFilter className="me-2" />
                  Category
                </label>
                <select
                  className="form-select"
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value as DocumentCategory | 'All')}
                >
                  <option value="All">All Categories</option>
                  <option value="Policy">Policy</option>
                  <option value="Procedure">Procedure</option>
                  <option value="Evidence">Evidence</option>
                  <option value="Regulation">Regulation</option>
                </select>
              </div>

              {/* Framework Filter */}
              <div className="col-md-3">
                <label className="form-label">Framework</label>
                <select
                  className="form-select"
                  value={selectedFramework}
                  onChange={e => setSelectedFramework(e.target.value as Framework | 'All')}
                >
                  <option value="All">All Frameworks</option>
                  <option value="ISO 27001">ISO 27001</option>
                  <option value="NIST">NIST</option>
                  <option value="GDPR">GDPR</option>
                  <option value="SOC 2">SOC 2</option>
                  <option value="PCI DSS">PCI DSS</option>
                  <option value="HIPAA">HIPAA</option>
                </select>
              </div>

              {/* Status Filter */}
              <div className="col-md-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={selectedStatus}
                  onChange={e => setSelectedStatus(e.target.value as DocumentStatus | 'All')}
                >
                  <option value="All">All Statuses</option>
                  <option value="Draft">Draft</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Approved">Approved</option>
                  <option value="Published">Published</option>
                  <option value="Expired">Expired</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* View Content */}
        {viewMode === 'tree' ? (
          <FileStructure 
            showHeader={false}
            searchTerm={searchTerm}
            selectedCategory={selectedCategory}
            selectedFramework={selectedFramework}
            selectedStatus={selectedStatus}
          />
        ) : (
          <>
            {/* Documents Grid */}
        <div className="row g-4">
          {filteredDocuments.length === 0 ? (
            <div className="col-12">
              <div className="card text-center p-5">
                <MdOutlineDescription size={64} className="text-muted mb-3" />
                <h5>No documents found</h5>
                <p className="text-muted mb-4">
                  {searchTerm || selectedCategory !== 'All' || selectedFramework !== 'All' || selectedStatus !== 'All'
                    ? 'Try adjusting your filters'
                    : 'Upload your first document to get started'}
                </p>
                {(!searchTerm && selectedCategory === 'All' && selectedFramework === 'All' && selectedStatus === 'All') && (
                  <button
                    className="btn btn-primary btn-lg d-flex align-items-center gap-2 mx-auto"
                    onClick={() => navigate('/documents/upload')}
                  >
                    <FaUpload size={18} /> Create New Document
                  </button>
                )}
              </div>
            </div>
          ) : (
            filteredDocuments.map(doc => (
              <div key={doc.metadata.id} className="col-md-6 col-lg-4">
                <div className="card h-100 document-card">
                  <div className="card-body">
                    {/* Document Header */}
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="d-flex align-items-center gap-2">
                        {getFileIcon(doc.versions[doc.versions.length - 1]?.fileName || '')}
                        <div>
                          <h6 className="mb-0">{doc.metadata.title}</h6>
                          <small className="text-muted">v{doc.metadata.currentVersion}</small>
                        </div>
                      </div>
                      {getStatusIcon(doc.metadata.status)}
                    </div>

                    {/* Description */}
                    {doc.metadata.description && (
                      <p className="text-muted small mb-3" style={{ minHeight: '40px' }}>
                        {doc.metadata.description.length > 100
                          ? doc.metadata.description.substring(0, 100) + '...'
                          : doc.metadata.description}
                      </p>
                    )}

                    {/* Metadata */}
                    <div className="mb-3">
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <span className="badge bg-secondary">{doc.metadata.category}</span>
                        <span className="badge bg-primary">{doc.metadata.framework}</span>
                        {doc.metadata.controlId && (
                          <span className="badge bg-info">{doc.metadata.controlId}</span>
                        )}
                        <span className={getStatusBadgeClass(doc.metadata.status)}>
                          {doc.metadata.status}
                        </span>
                      </div>
                      <div className="small text-muted">
                        <div>Owner: {doc.metadata.documentOwnerName}</div>
                        <div>Updated: {formatDate(doc.metadata.updatedAt)}</div>
                        {doc.metadata.expiryDate && (
                          <div
                            className={
                              isExpired(doc.metadata.expiryDate)
                                ? 'text-danger fw-bold'
                                : isExpiringSoon(doc.metadata.expiryDate)
                                ? 'text-warning fw-bold'
                                : ''
                            }
                          >
                            Expires: {formatDate(doc.metadata.expiryDate)}
                            {isExpired(doc.metadata.expiryDate) && ' (Expired)'}
                            {isExpiringSoon(doc.metadata.expiryDate) && !isExpired(doc.metadata.expiryDate) && ' (Expiring Soon)'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary flex-fill"
                        onClick={() => navigate(`/documents/${doc.metadata.id}`)}
                      >
                        <FaEye className="me-1" />
                        View Details
                      </button>
                      {doc.metadata.status === 'Draft' && (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => navigate(`/documents/${doc.metadata.id}/edit`)}
                        >
                          <FaEdit />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

            {/* Summary */}
            {filteredDocuments.length > 0 && (
              <div className="mt-4 text-muted small">
                Showing {filteredDocuments.length} of {documents.length} documents
              </div>
            )}
          </>
        )}

        {/* Document Chat Assistant */}
        <DocumentChat
          onDocumentClick={(documentId) => navigate(`/documents/${documentId}`)}
        />
      </div>
    </div>
  );
};

export default DocumentList;

